import React from 'react';
import { ELEMENT_TYPES } from './elements';

export interface ShapeEntry {
  type: string;
  label: string;
}

export const SHAPE_GROUPS: Record<string, ShapeEntry[]> = {
  'Геометрия': [
    { type: ELEMENT_TYPES.TRIANGLE, label: 'Треугольник' },
    { type: ELEMENT_TYPES.DIAMOND, label: 'Ромб' },
    { type: ELEMENT_TYPES.PARALLELOGRAM, label: 'Параллелограмм' },
    { type: ELEMENT_TYPES.ROUNDED_RECT, label: 'Скругленный' },
    { type: ELEMENT_TYPES.ELLIPSE, label: 'Эллипс' },
    { type: ELEMENT_TYPES.CYLINDER, label: 'Цилиндр' },
    { type: ELEMENT_TYPES.CLOUD, label: 'Облако' },
    { type: ELEMENT_TYPES.CALLOUT, label: 'Выноска' },
    { type: ELEMENT_TYPES.TRAPEZOID, label: 'Трапеция' },
  ],
  'Блок-схемы': [
    { type: ELEMENT_TYPES.PROCESS, label: 'Процесс' },
    { type: ELEMENT_TYPES.DECISION, label: 'Решение' },
    { type: ELEMENT_TYPES.TERMINATOR, label: 'Терминатор' },
    { type: ELEMENT_TYPES.DOCUMENT, label: 'Документ' },
    { type: ELEMENT_TYPES.DATA, label: 'Данные' },
    { type: ELEMENT_TYPES.DATABASE, label: 'База данных' },
    { type: ELEMENT_TYPES.MANUAL_INPUT, label: 'Ввод' },
    { type: ELEMENT_TYPES.PREPARATION, label: 'Подготовка' },
    { type: ELEMENT_TYPES.DISPLAY, label: 'Дисплей' },
    { type: ELEMENT_TYPES.INTERNAL_STORAGE, label: 'Память' },
    { type: ELEMENT_TYPES.QUEUE, label: 'Очередь' },
  ],
  'UML / Прочее': [
    { type: ELEMENT_TYPES.ACTOR, label: 'Актор' },
    { type: ELEMENT_TYPES.USE_CASE, label: 'Use Case' },
    { type: ELEMENT_TYPES.PACKAGE, label: 'Пакет' },
    { type: ELEMENT_TYPES.COMPONENT, label: 'Компонент' },
    { type: ELEMENT_TYPES.INTERFACE, label: 'Интерфейс' },
    { type: ELEMENT_TYPES.STEP, label: 'Шаг' },
    { type: ELEMENT_TYPES.LOOP_LIMIT, label: 'Цикл' },
    { type: ELEMENT_TYPES.STORAGE, label: 'Хранилище' },
  ],
};

// Reverse lookup: type → group shapes
const _typeToGroup = new Map<string, ShapeEntry[]>();
for (const shapes of Object.values(SHAPE_GROUPS)) {
  for (const shape of shapes) {
    _typeToGroup.set(shape.type, shapes);
  }
}

/** Returns the group shapes for a given element type, or null if not in any group. */
export const getGroupForType = (type: string): ShapeEntry[] | null =>
  _typeToGroup.get(type) ?? null;

export const ShapeIcon: React.FC<{ type: string; color: string }> = ({ type, color }) => {
  const style = { fill: 'none', stroke: color, strokeWidth: 2 } as React.SVGAttributes<SVGElement>;

  switch (type) {
    case ELEMENT_TYPES.TRIANGLE:
      return <svg width="24" height="24" viewBox="0 0 24 24"><path d="M12 2 L22 22 L2 22 Z" {...style} /></svg>;
    case ELEMENT_TYPES.DIAMOND:
    case ELEMENT_TYPES.DECISION:
      return <svg width="24" height="24" viewBox="0 0 24 24"><path d="M12 2 L22 12 L12 22 L2 12 Z" {...style} /></svg>;
    case ELEMENT_TYPES.PARALLELOGRAM:
    case ELEMENT_TYPES.DATA:
      return <svg width="24" height="24" viewBox="0 0 24 24"><path d="M6 4 L22 4 L18 20 L2 20 Z" {...style} /></svg>;
    case ELEMENT_TYPES.ROUNDED_RECT:
    case ELEMENT_TYPES.TERMINATOR:
      return <svg width="24" height="24" viewBox="0 0 24 24"><rect x="2" y="6" width="20" height="12" rx="6" {...style} /></svg>;
    case ELEMENT_TYPES.ELLIPSE:
    case ELEMENT_TYPES.USE_CASE:
      return <svg width="24" height="24" viewBox="0 0 24 24"><ellipse cx="12" cy="12" rx="10" ry="6" {...style} /></svg>;
    case ELEMENT_TYPES.CYLINDER:
    case ELEMENT_TYPES.DATABASE:
      return <svg width="24" height="24" viewBox="0 0 24 24"><path d="M12 4 C17.5 4 22 5.8 22 8 V16 C22 18.2 17.5 20 12 20 C6.5 20 2 18.2 2 16 V8 C2 5.8 6.5 4 12 4 Z M2 8 C2 10.2 6.5 12 12 12 C17.5 12 22 10.2 22 8" {...style} /></svg>;
    case ELEMENT_TYPES.CLOUD:
      return <svg width="24" height="24" viewBox="0 0 24 24"><path d="M6 18 C3 18 1 16 1 13.5 C1 11 3 9 5 9 C5.5 5 9 3 12 3 C16 3 19 5.5 19.5 9 C21.5 9 23 11 23 13.5 C23 16 21 18 18 18 Z" {...style} /></svg>;
    case ELEMENT_TYPES.CALLOUT:
      return <svg width="24" height="24" viewBox="0 0 24 24"><path d="M2 4 H22 V16 H14 L8 22 V16 H2 Z" {...style} /></svg>;
    case ELEMENT_TYPES.TRAPEZOID:
      return <svg width="24" height="24" viewBox="0 0 24 24"><path d="M6 4 L18 4 L22 20 L2 20 Z" {...style} /></svg>;
    case ELEMENT_TYPES.PROCESS:
      return <svg width="24" height="24" viewBox="0 0 24 24"><rect x="2" y="6" width="20" height="12" {...style} /></svg>;
    case ELEMENT_TYPES.DOCUMENT:
      return <svg width="24" height="24" viewBox="0 0 24 24"><path d="M4 2 H20 V16 Q16 22 12 16 Q8 10 4 16 Z" {...style} /></svg>;
    case ELEMENT_TYPES.MANUAL_INPUT:
      return <svg width="24" height="24" viewBox="0 0 24 24"><path d="M2 8 L22 2 V22 H2 Z" {...style} /></svg>;
    case ELEMENT_TYPES.PREPARATION:
      return <svg width="24" height="24" viewBox="0 0 24 24"><path d="M2 12 L7 4 H17 L22 12 L17 20 H7 Z" {...style} /></svg>;
    case ELEMENT_TYPES.DISPLAY:
      return <svg width="24" height="24" viewBox="0 0 24 24"><path d="M2 12 L6 4 H18 Q22 4 22 12 Q22 20 18 20 H6 Z" {...style} /></svg>;
    case ELEMENT_TYPES.INTERNAL_STORAGE:
      return <svg width="24" height="24" viewBox="0 0 24 24"><rect x="2" y="4" width="20" height="16" {...style} /><line x1="6" y1="4" x2="6" y2="20" {...style} /><line x1="2" y1="8" x2="22" y2="8" {...style} /></svg>;
    case ELEMENT_TYPES.QUEUE:
      return <svg width="24" height="24" viewBox="0 0 24 24"><path d="M16 4 H2 Q2 12 2 20 H16 A8 8 0 0 0 16 4" {...style} /></svg>;
    case ELEMENT_TYPES.ACTOR:
      return <svg width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="5" r="3" {...style} /><path d="M12 8 V16 M6 10 H18 M12 16 L8 22 M12 16 L16 22" {...style} /></svg>;
    case ELEMENT_TYPES.PACKAGE:
      return <svg width="24" height="24" viewBox="0 0 24 24"><path d="M2 6 H10 V2 H2 Z M2 6 V20 H22 V6 H10" {...style} /></svg>;
    case ELEMENT_TYPES.COMPONENT:
      return <svg width="24" height="24" viewBox="0 0 24 24"><rect x="6" y="4" width="16" height="16" {...style} /><rect x="2" y="6" width="8" height="4" {...style} /><rect x="2" y="14" width="8" height="4" {...style} /></svg>;
    case ELEMENT_TYPES.INTERFACE:
      return <svg width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" {...style} /><line x1="4" y1="12" x2="20" y2="12" {...style} /></svg>;
    case ELEMENT_TYPES.STEP:
      return <svg width="24" height="24" viewBox="0 0 24 24"><path d="M2 6 H18 L22 12 L18 18 H2 Z" {...style} /></svg>;
    case ELEMENT_TYPES.LOOP_LIMIT:
      return <svg width="24" height="24" viewBox="0 0 24 24"><path d="M2 6 H18 L22 10 L22 20 H2 Z" {...style} /></svg>;
    case ELEMENT_TYPES.STORAGE:
      return <svg width="24" height="24" viewBox="0 0 24 24"><path d="M2 4 A4 8 0 0 0 2 20 H22 A4 8 0 0 0 22 4 H2" {...style} /></svg>;
    default:
      return <svg width="24" height="24" viewBox="0 0 24 24"><rect x="4" y="6" width="16" height="12" {...style} /></svg>;
  }
};
